import React from 'react';
import {
  AppBar as MuiAppBar,
  Badge,
  Box,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import UserAvatar from './UserAvatar';
import { getAuthenticatedHomePath, hasOnlyDataExchangeReaderRole } from '../config/roleAccess';

function getRoleLabel(role) {
  const labels = {
    admin: 'Admin',
    leiter: 'Leiter der Höhenrettung',
    lehrhöhenretter_ausbildung: 'Lehrhöhenretter, Ber. Aus. & Fortb.',
    lehrhöhenretter_technik: 'Lehrhöhenretter, Ber. Tech.',
    lehrhöhenretter_einsatztaktik: 'Lehrhöhenretter, Ber. Eins.',
    lehrhöhenretter: 'Lehrhöhenretter',
    einheitsführer: 'Einheitsführer',
    geräteprüfer: 'Geräteprüfer',
    höhenretter: 'Höhenretter',
    bewerber_srht: 'Bewerber SRHT',
    bewerber_srht_extern: 'Bewerber SRHT (Extern)',
    beobachter: 'Beobachter',
    extern_partner: 'Externer Partner',
    datenaustausch_leser: 'Datenaustausch (nur Lesen)',
  };
  return labels[role] || role;
}

function getWachbereitschaftLabel(wachbereitschaft) {
  if (wachbereitschaft === '1_wb') return '1. Wachbereitschaft';
  if (wachbereitschaft === '2_wb') return '2. Wachbereitschaft';
  return '';
}

function AppBarComponent({ onDrawerOpen }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const isDataExchangeReader = hasOnlyDataExchangeReaderRole(user);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleHome = () => navigate(getAuthenticatedHomePath(user));

  const roleDisplay = user?.roles?.length
    ? user.roles.map((role) => getRoleLabel(role)).join(', ')
    : 'Keine Rolle';

  const wbLabel = getWachbereitschaftLabel(user?.wachbereitschaft);

  const tooltipContent = (
    <Box>
      <Box>{roleDisplay}</Box>
      {wbLabel && <Box>{wbLabel}</Box>}
    </Box>
  );

  return (
    <MuiAppBar position="sticky">
      <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <img
              src="/feuerwehr_duesseldorf.png"
              alt="Feuerwehr Düsseldorf Logo"
              style={{ height: isMobile ? 40 : 48, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
              <Tooltip title="Zur Startseite">
                <IconButton onClick={handleHome} sx={{ color: '#fff', minHeight: 44, minWidth: 44 }}>
                  <HomeIcon fontSize="large" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Menü öffnen">
                <IconButton onClick={onDrawerOpen} sx={{ color: '#fff', minHeight: 44, minWidth: 44 }}>
                  <MenuIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
          {!isDataExchangeReader && (
            <Tooltip title="Benachrichtigungen">
              <IconButton onClick={() => navigate('/benachrichtigungen')} sx={{ color: '#fff', minHeight: 44, minWidth: 44 }}>
                <Badge badgeContent={unreadCount} color="error" max={99} data-testid="notification-badge">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 1.5 }}>
            <img
              src="/logo.jpeg"
              alt="Höhenrettung Logo"
              style={{
                height: isMobile ? 54 : 84,
                objectFit: 'contain',
                border: '2px solid #fff',
                borderRadius: 8,
                background: 'rgba(0,0,0,0.1)',
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: isMobile ? 13 : 16, maxWidth: isMobile ? 100 : 'none', textAlign: 'center' }}>
                {user?.fullName || user?.full_name || 'Benutzer'}
              </Typography>
              <Tooltip title={tooltipContent}>
                <Box>
                  <UserAvatar user={user} size={isMobile ? 34 : 40} />
                </Box>
              </Tooltip>
            </Box>
          </Box>

          <Tooltip title="Abmelden">
            <IconButton onClick={handleLogout} sx={{ color: '#fff', minHeight: 44, minWidth: 44 }}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
}

export default AppBarComponent;
